import React, { FC, useCallback, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { ReactComponent as ArrowCircleRight } from '../../../icons/arrow-right-circle.svg';
import { ReactComponent as CoinsIcon } from '../../../icons/coins.svg';
import { ReactComponent as DreamLogoCard } from '../../../icons/dream-logo-icon.svg';
import api from '../../../api';
import {
  DeliveryAreaSupportedResponse,
  CompleteTaskIntroResponse,
  TaskType,
  TaskChatMessage,
} from '@dreamai/types';
import { OnboardingStepProps } from '../OnboardingFlow';
import { useOrganisationsEnabled } from '../../../hooks/useOrganisationsEnabled';
import TaskModal from './TaskModal';
import './card-intro-step.scss';

interface CardIntroStepProps extends OnboardingStepProps {
  assistantNickname: string | null;
  assistantName: string | null;
  assistantPredominantColour: string | null;
  userName: string | null;
}

const CardIntroStep: FC<CardIntroStepProps> = ({
  refreshStatus,
  assistantNickname,
  assistantName,
  assistantPredominantColour,
  userName,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const organisationsEnabled = useOrganisationsEnabled();

  const displayNickname = assistantNickname || 'Your secretary';
  const displayAssistantName = assistantName || 'Your secretary';
  const displayUserName = userName || '';
  const displayFirstName = displayUserName.split(' ')[0] || displayUserName;

  const [showModal, setShowModal] = useState(false);
  const [areaSupported, setAreaSupported] = useState(true);

  // Check delivery area support
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getAccessTokenSilently();
        const { data } = await api.get<DeliveryAreaSupportedResponse>(
          '/onboarding/delivery-area-supported',
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!cancelled) setAreaSupported(data.supported);
      } catch (err) {
        console.error('[CardIntro] Failed to check delivery area:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getAccessTokenSilently]);

  const handleComplete = useCallback(
    async (
      taskType: TaskType,
      summary: string | null,
      chatMessages: TaskChatMessage[],
    ) => {
      try {
        const token = await getAccessTokenSilently();
        await api.post<CompleteTaskIntroResponse>(
          '/onboarding/complete-task-intro',
          { taskType, preferencesSummary: summary, messages: chatMessages },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        sessionStorage.setItem('onboarding_task_selected', 'true');
        refreshStatus();
      } catch (err) {
        console.error('[CardIntro] Failed to complete task intro:', err);
      }
    },
    [getAccessTokenSilently, refreshStatus],
  );

  const handleSkip = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      await api.post<CompleteTaskIntroResponse>(
        '/onboarding/complete-task-intro',
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      sessionStorage.removeItem('onboarding_task_selected');
      refreshStatus();
    } catch (err) {
      console.error('[CardIntro] Failed to skip task intro:', err);
    }
  }, [getAccessTokenSilently, refreshStatus]);

  return (
    <div className='onboarding-step card-intro-step'>
      <div className='card-intro-layout'>
        <div className='card-intro-left'>
          <h1 className='card-intro-title'>Shop, book, reserve. With me.</h1>
          <div className='card-intro-description'>
            <p>
              Ask me to buy something, and I'll make it happen. Whether it's
              online, or something in the real world I'm able to find, buy, and
              deliver what you need.
            </p>
            <p>
              To make purchases you'll either need to top up my card, or add a
              payment method.
            </p>
            <p>
              <span className='card-intro-medium'>
                To show you how easily I can buy or book things let me buy you a
                treat –{' '}
              </span>
              <span className='card-intro-highlight'>
                - a little something on me.
              </span>
              <span className='card-intro-medium'>.</span>
            </p>
          </div>
          <div className='card-intro-actions'>
            <button className='step-button' onClick={() => setShowModal(true)}>
              Get started
              <ArrowCircleRight />
            </button>
          </div>
        </div>

        <div className='card-intro-right'>
          <div className='card-demo-frame'>
            <div className='card-tilt-wrapper'>
              <div className='card-visual'>
                <div className='card-header'>
                  <CoinsIcon className='card-coins-icon' />
                  <span className='card-name'>{displayNickname}'s Card</span>
                </div>
                <DreamLogoCard className='card-dream-logo' />
                <img
                  src='https://d12b9vnf4u4q7j.cloudfront.net/chip.png'
                  alt=''
                  className='card-chip'
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {showModal && (
        <TaskModal
          areaSupported={areaSupported}
          assistantName={displayAssistantName}
          assistantPredominantColour={assistantPredominantColour}
          userName={displayFirstName}
          onComplete={handleComplete}
          onSkip={handleSkip}
        />
      )}
    </div>
  );
};

export default CardIntroStep;
